/**
 * Vertikale Skalierung des Höhenprofils — gemeinsam für Web (`GpxMap`) und App
 * (`RouteProfile`), damit beide dieselbe Strecke gleich hoch zeichnen.
 *
 * Die Aufgabe ist ein Kompromiss zwischen zwei Fehlern:
 *
 * 1. Streckt man die Datenspanne formatfüllend, sieht jede Hügelkette nach Hochgebirge
 *    aus — 80 Höhenmeter über 200 km füllen dann dieselbe Fläche wie ein Alpenpass.
 * 2. Koppelt man die Anzeigespanne stattdessen an die Distanz (annähernd konstante,
 *    „ehrliche" Überhöhung), wächst sie bei langen Strecken ungebremst: eine flache
 *    300-km-Runde bekam so eine Achse von über 2400 m für 280 m echte Spanne und lag
 *    als gerader Strich auf der Grundlinie.
 *
 * Deshalb gilt die distanzbezogene Überhöhung nur bis zu einem Deckel: das Profil
 * belegt immer mindestens 1/MAX_FLATTEN der Plothöhe, egal wie lang die Strecke ist.
 */

/** Meter Anzeigespanne pro km Distanz — Maß der angestrebten Überhöhung. */
const ELEV_RANGE_PER_KM = 8;

/**
 * Obergrenze der Überhöhung als Vielfaches der echten Spanne. 2,5 heißt: die Daten
 * füllen nie weniger als 40 % der Plothöhe.
 */
const MAX_FLATTEN = 2.5;

/**
 * Absolute Untergrenze der Anzeigespanne in Metern. Darunter ist ein Profil
 * Messrauschen und darf nicht bildfüllend gezeichnet werden, egal wie kurz die
 * Strecke ist.
 */
const MIN_RANGE_M = 60;

/** Luft über und unter dem Profil, damit die Linie nicht am Rand klebt. */
const RANGE_PADDING = 1.15;

export interface ElevationBand {
  /** Unterer Rand der Höhenachse in Metern. */
  lo: number;
  /** Oberer Rand der Höhenachse in Metern. */
  hi: number;
}

/**
 * Höhenachse für ein Profil bestimmen.
 *
 * @param minEle  tiefster Punkt der Strecke in Metern
 * @param maxEle  höchster Punkt der Strecke in Metern
 * @param totalKm Länge der Strecke in km
 */
export function elevationBand(minEle: number, maxEle: number, totalKm: number): ElevationBand {
  const dataRange = Math.max(1, maxEle - minEle);
  const displayRange = Math.max(
    dataRange * RANGE_PADDING,
    MIN_RANGE_M,
    Math.min(Math.max(0, totalKm) * ELEV_RANGE_PER_KM, dataRange * MAX_FLATTEN)
  );

  // Profil mittig in die Spanne legen …
  let lo = minEle - (displayRange - dataRange) / 2;

  // … aber nicht unter Meereshöhe, wenn die Strecke nicht dorthin führt. Die Grenze ist
  // der tiefste Punkt, nicht die Null: eine Strecke, die wirklich unter Meereshöhe
  // verläuft, behält ihre Achse — nur eben nicht tiefer als nötig.
  const floor = Math.min(0, minEle);
  if (lo < floor) lo = floor;

  return { lo, hi: lo + displayRange };
}

/**
 * Höhenmeter zur Anzeige, mit Tausenderpunkt: `1.234 hm`. Gemeinsam für Web und App, damit
 * beide dieselbe Zahl gleich schreiben. Ohne Angabe (fehlend, 0) kommt `null` zurück, den
 * Ersatz wählt die Stelle selbst. Bewusst ohne `toLocaleString`, damit das Ergebnis nicht an
 * der Intl-Ausstattung der JS-Engine hängt.
 */
export function formatElevationMeters(meters?: number | null): string | null {
  if (meters == null || !(meters > 0)) return null;
  return `${String(Math.round(meters)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} hm`;
}
